import {
  Document,
  PDFViewer,
  Page as PDFPage,
  StyleSheet,
  Text as PDFText,
  View as PDFView,
  Font,
  Image as PDFImage
} from '@react-pdf/renderer';
import React from "react";
import JsBarcode from "jsbarcode";
import QRCode from "qrcode";

Font.register({
  family: 'Roboto',
  fonts: [{ src: '/Roboto-Bold.ttf', fontWeight: 'medium' }]
});

const fullScreenStyles = {
  container: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    zIndex: 1000,
    backgroundColor: 'white'
  }
};

const styles = StyleSheet.create({
  page: {
    padding: 10,
    paddingTop: '13%',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  text: {
    fontSize: 10
  },
  textUnderlined: {
    fontSize: 10,
    textDecoration: 'underline',
    textDecorationColor: 'black',
    textDecorationThickness: 1.5,
    width: '120%'
  },
  heading: {
    fontSize: 14,
    fontWeight: 'bold',
    textAlign: 'center'
  },
  contentContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderRadius: '5px',
    border: '1px solid black',
    width: '100%',
    gap: 120,
    paddingTop: '5px',
    paddingBottom: '3px',
    paddingRight: '40px',
    paddingLeft: '40px',
    position: 'relative'
  },
  column: {
    width: '50%',
    display: 'flex',
    flexDirection: 'column',
    gap: 2
  },
  column1: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    height: "68%",
    marginRight: "72%",
    gap: 15,
    marginTop: "1%",
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 1,
    width: '100%'
  },
  label: {
    fontWeight: 'bold',
    fontSize: 10
  },
  value: {
    fontSize: 10,
    fontWeight: 'normal',
    width: '65%'
  },
  leftColumn: {
    width: '50%',
    display: 'flex',
    flexDirection: 'column',
    gap: 0
  }
});

const Page = ({ style, children, ...props }) => (
  <PDFPage style={{ ...styles.page, ...style }} {...props}>
    {children}
  </PDFPage>
);

const View = ({ style, children, ...props }) => (
  <PDFView style={{ ...style }} {...props}>
    {children}
  </PDFView>
);

const Text = ({ style, content, ...props }) => (
  <PDFText style={{ ...styles.text, ...style }} {...props}>
    {content}
  </PDFText>
);

export const PatientRegistrationPdf10 = ({ data, isPublic = false }) => {
  const [barcodeImg, setBarcodeImg] = React.useState("");
  const [qrImg, setQrImg] = React.useState("");
  const [isDownloading, setIsDownloading] = React.useState(false);
  const [isDownloaded, setIsDownloaded] = React.useState(false);
  const [isError, setIsError] = React.useState(false);


  function formatDate(dateString) {
    const date = new Date(dateString);
    const istParts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).formatToParts(date);
    const day = istParts.find((p) => p.type === 'day').value;
    const month = istParts.find((p) => p.type === 'month').value;
    const year = istParts.find((p) => p.type === 'year').value;
    return `${day}/${month}/${year}`;
  }

  const ageString = data.age;
  const years = ageString.match(/\d+Y/);

  const addDaysToDate = (date, days) => {
    const newDate = new Date(date);
    newDate.setDate(newDate.getDate() + days);
    return newDate;
  };

  const validityDate = addDaysToDate(data.op_date, 6);

  const formatRegTime = (time) => {
    const [hours, minutes] = time.split(':');
    const date = new Date();
    date.setHours(hours);
    date.setMinutes(minutes);

    // Format to 12-hour time
    const options = { hour: '2-digit', minute: '2-digit', hour12: true };
    return date.toLocaleTimeString('en-US', options);
  }




  React.useEffect(() => {
    if (!data || typeof window === "undefined") return;

    const params = new URLSearchParams(window.location.search);
    const refId = params.get("refId");

    // BARCODE
    const canvas = document.createElement("canvas");
    JsBarcode(canvas, String(data.receiptNo), {
      format: "CODE128",
      displayValue: false,
      height: 40
    });
    setBarcodeImg(canvas.toDataURL());
    let path;
    // QR → PUBLIC LINK
    if (refId) {
      if (isPublic) {
        path = window.location.pathname;
      } else {
        path = window.location.pathname.replace("/print/", "/public/print/");
      }
      const qrUrl = `${window.location.origin}${path}?refId=${refId}`;
      QRCode.toDataURL(qrUrl).then(setQrImg);
    }
  }, [data, isPublic]);


  const downloadPDF = async () => {
    try {
      // Import pdf dynamically to avoid SSR issues
      const { pdf } = await import('@react-pdf/renderer');
      const blob = await pdf(PdfDoc).toBlob();
      const url = URL.createObjectURL(blob);

      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.download = `OPD-Bill-${data.receiptNo || Date.now()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      setTimeout(() => {
        URL.revokeObjectURL(url);
        setIsDownloading(false);

        // Show success message instead of trying to close
        setIsDownloaded(true);
      }, 100);

    } catch (error) {
      console.error('Error downloading PDF:', error);
      setIsDownloading(false);
      setIsError(true);
      //alert('Failed to download PDF. Please try again.');
    }
  };


  React.useEffect(() => {
    if (isPublic && data && barcodeImg && qrImg && !isDownloading) {
      // Small delay to ensure everything is loaded
      const timer = setTimeout(() => {
        downloadPDF();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [isPublic, data, barcodeImg, qrImg, isDownloading]);

  const PdfDoc = (
    <Document title={`${data.name} Registration`} style={{ border: '3px solid black' }}>
      <Page size="A4" style={styles.page}>
        <View
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 0,
            marginTop: '3%',
          }}
        >
          {/* Background / Logo Image */}
          <PDFImage
            src="/PDF Print Format.png"
            style={{
              width: '85%',
              height: '100%',
            }}
          />

          {/* QR Code OVER the logo */}
          {qrImg && (
            <PDFImage
              src={qrImg}
              style={{
                position: 'absolute',
                top: 20,       // adjust as needed
                right: 10,     // or left: 40
                width: 70,
                height: 70,
                zIndex: 10,
              }}
            />
          )}
        </View>

        <View
          style={{
            border: '1px solid black',
            paddingBottom: '2px',
            paddingLeft: '50px',
            paddingRight: '50px',
            marginBottom: 3,
            boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.9)'
          }}
        >
          <Text
            content="OPD Case Paper"
            style={{
              ...styles.label,
              fontFamily: 'Roboto',
              fontSize: '15px'
            }}
          />
        </View>

        <View style={{ ...styles.contentContainer, border: '1px solid black' }}>
          <View style={styles.column}>
            <View style={styles.row}>
              <Text
                content="UHID                     :             "
                style={{ ...styles.label, fontFamily: 'Roboto' }}
              />
              <Text
                content={data.uhid}
                style={{
                  ...styles.value,
                  textAlign: 'start',
                  paddingBottom: '2px'
                }}
              />
            </View>
            <View style={styles.row}>
              <Text content="Patient Name      :           " style={{ ...styles.label, fontFamily: 'Roboto' }} />
              <Text
                content={data.name}
                style={{
                  ...styles.value,
                  textAlign: 'start',
                  paddingBottom: '2px'
                }}
              />
            </View>
            <View style={styles.row}>
              <Text
                content="Gender                 :            "
                style={{ ...styles.label, fontFamily: 'Roboto' }}
              />
              <Text
                content={data.gender}
                style={{
                  ...styles.value,
                  textAlign: 'start',
                  paddingBottom: '2px'
                }}
              />
            </View>
            <View style={styles.row}>
              <Text
                content="Age                       :            "
                style={{ ...styles.label, fontFamily: 'Roboto' }}
              />
              <Text
                content={data.age}
                style={{
                  ...styles.value,
                  textAlign: 'start',
                  paddingBottom: '2px'
                }}
              />
            </View>
            <View style={styles.row}>
              <Text
                content="Phone No            :            "
                style={{ ...styles.label, fontFamily: 'Roboto' }}
              />
              <Text
                content={data.mobile}
                style={{
                  ...styles.value,
                  textAlign: 'start',
                  paddingBottom: '2px'
                }}
              />
            </View>
            <View style={{ ...styles.row, width: "135%" }}>
              <Text
                content="Address              :      "
                style={{ ...styles.label, fontFamily: 'Roboto', position: 'relative', left: '7%' }}
              />
              <Text content={`${data.address}`} style={{ ...styles.value, textAlign: 'start' }} />
            </View>
            <View style={{ ...styles.row, width: "135%" }}>
              <Text
                content="Consultant          :"
                style={{ ...styles.label, fontFamily: 'Roboto' }}
              />
              <Text
                content={`${data.consultant}(${data?.doctor_qualification})`}
                style={{
                  ...styles.value,
                  textAlign: 'start',
                  paddingBottom: '2px'
                }}
              />
            </View>
          </View>

          <View style={styles.column}>
            <View style={styles.row}>
              <Text
                content="Reg Date              :            "
                style={{ ...styles.label, fontFamily: 'Roboto', position: 'relative', left: '7%' }}
              />
              <Text
                content={`${formatDate(data.first_reg_date)}`}
                style={{
                  ...styles.value,
                  textAlign: 'start',
                  paddingBottom: '2px'
                }}
              />
            </View>
            <View style={{ ...styles.row }}>
              <Text
                content="Visit Type            :              "
                style={{ ...styles.label, fontFamily: 'Roboto', position: 'relative', left: '7%' }}
              />
              <Text
                content={`${data.opd_type}`}
                style={{ ...styles.value, textAlign: 'start', paddingBottom: '2px' }}
              />
            </View>
            <View style={{ ...styles.row }}>
              <Text
                content="Visit  No               :             "
                style={{ ...styles.label, fontFamily: 'Roboto', position: 'relative', left: '7%' }}
              />
              <Text
                content={`${data.visit_no}`}
                style={{ ...styles.value, textAlign: 'start', paddingBottom: '2px' }}
              />
            </View>
            <View style={{ ...styles.row }}>
              <Text
                content="Visit Date             :            "
                style={{ ...styles.label, fontFamily: 'Roboto', position: 'relative', left: '7%' }}
              />
              <Text
                content={`${formatDate(data.reg_date)} ${formatRegTime(data.reg_time)} `}
                style={{ ...styles.value, textAlign: 'start', paddingBottom: '2px' }}
              />
            </View>
            <View style={{ ...styles.row, width: "135%" }}>
              <Text
                content="Department         :    "
                style={{ ...styles.label, fontFamily: 'Roboto', position: 'relative', left: '7%' }}
              />
              <Text
                content={`${data.department}`}
                style={{ ...styles.value, textAlign: 'start', paddingBottom: '2px' }}
              />
            </View>

            <View style={{ ...styles.row }}>
              <Text
                content="Referred by          :           "
                style={{ ...styles.label, fontFamily: 'Roboto', position: 'relative', left: '7%' }}
              />
              <Text
                content={`${data.ref_by}`}
                style={{ ...styles.value, textAlign: 'start', paddingBottom: '2px' }}
              />
            </View>
            <View style={{ ...styles.row }}>
              <Text
                content="Total Paid             :           "
                style={{ ...styles.label, fontFamily: 'Roboto', position: 'relative', left: '7%' }}
              />
              <Text content={`${data?.total}`} style={{ ...styles.value, textAlign: 'start', paddingBottom: '2px' }} />
            </View>
          </View>
        </View>
        <View
          style={{
            width: '100%',
            borderBottom: '1px solid black',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: '4%',
            flexDirection: 'row',
            paddingHorizontal: '3%'
          }}
        >
          <Text
            style={{
              fontFamily: 'Roboto',
              textAlign: 'left'
            }}
            content="Date"
          />
          <Text
            style={{
              fontFamily: 'Roboto',
              textAlign: 'center',
              flex: 1
            }}
            content="Examination/Medication"
          />
        </View>
        <View style={{ ...styles.column1, borderRight: '1px solid black', width: '23%' }}>
          <Text content="BP" style={{ fontFamily: 'Roboto' }} />
          <Text content="Pulse" style={{ fontFamily: 'Roboto' }} />
          <Text content="SPO2" style={{ fontFamily: 'Roboto' }} />
          <Text content="TEMP" style={{ fontFamily: 'Roboto' }} />
          <Text content="GRBS" style={{ fontFamily: 'Roboto' }} />
          <Text content="WEIGHT" style={{ fontFamily: 'Roboto' }} />
          <Text content="INVESTIGATION" style={{ fontFamily: 'Roboto', marginTop: "10%", textDecoration: "underline" }} />
          <Text content="CBC" style={{ fontFamily: 'Roboto' }} />
          <Text content="Blood Group" style={{ fontFamily: 'Roboto' }} />
          <Text content="LFT" style={{ fontFamily: 'Roboto' }} />
          <Text content="Urea/Creat" style={{ fontFamily: 'Roboto' }} />
          <Text content="HIV/HCV/HBsAg" style={{ fontFamily: 'Roboto' }} />
          <Text content="ECG" style={{ fontFamily: 'Roboto' }} />
          <Text content="X-Ray" style={{ fontFamily: 'Roboto' }} />
        </View>
        <View
          style={{
            width: '100%',
            borderBottom: '1px solid black',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: '4%',
            flexDirection: 'row',
            paddingHorizontal: '3%'
          }}
        >
          <Text
            style={{
              fontFamily: 'Roboto',
              textAlign: 'left'
            }}
            content={`Validity : 1 Consultation(s) before ${formatDate(validityDate)}`}
          />
        </View>
      </Page>

    </Document>
  );


  if (isPublic) {
    return (
      <div style={{
        backgroundColor: "white",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "Arial, sans-serif",
        padding: "20px",
        textAlign: "center"
      }}>
        <div style={{ marginBottom: "20px" }}>
          <img
            src="/logo_Hospital_m.jpeg"
            alt="Hospital Logo"
            style={{ width: "200px", height: "80px", objectFit: "contain" }}
          />
        </div>

        {isDownloaded ? (
          <div style={{
            padding: "30px",
            backgroundColor: "#f0f9f0",
            borderRadius: "8px",
            maxWidth: "400px",
            border: "1px solid #4CAF50"
          }}>
            <div style={{ fontSize: "24px", color: "#4CAF50", marginBottom: "15px" }}>
              ✓
            </div>
            <div style={{ fontSize: "18px", fontWeight: "bold", color: "#4CAF50", marginBottom: "10px" }}>
              PDF Downloaded Successfully!
            </div>
            <p style={{ fontSize: "14px", color: "#666", marginBottom: "15px" }}>
              Your bill has been downloaded. You can now close this window.
            </p>
            <button
              onClick={() => window.location.href = "about:blank"}
              style={{
                backgroundColor: "#4CAF50",
                color: "white",
                border: "none",
                padding: "10px 20px",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "bold",
                marginTop: "10px"
              }}
            >
              Close This Page
            </button>
          </div>
        ) : isError ? (
          <div style={{
            padding: "30px",
            backgroundColor: "#fff5f5",
            borderRadius: "8px",
            maxWidth: "400px",
            border: "1px solid #f44336"
          }}>
            <div style={{ fontSize: "24px", color: "#f44336", marginBottom: "15px" }}>
              ✗
            </div>
            <div style={{ fontSize: "18px", fontWeight: "bold", color: "#f44336", marginBottom: "10px" }}>
              Download Failed
            </div>
            <p style={{ fontSize: "14px", color: "#666", marginBottom: "15px" }}>
              Failed to download PDF. Please try again.
            </p>
            <button
              onClick={downloadPDF}
              style={{
                backgroundColor: "#1976D2",
                color: "white",
                border: "none",
                padding: "10px 20px",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "bold",
                marginTop: "10px"
              }}
            >
              Try Again
            </button>
          </div>
        ) : (
          <div style={{
            padding: "20px",
            backgroundColor: "#f5f5f5",
            borderRadius: "8px",
            maxWidth: "400px"
          }}>
            <div style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px" }}>
              {isDownloading ? "Downloading PDF..." : "Preparing PDF for download..."}
            </div>

            {isDownloading && (
              <div style={{ margin: "20px 0" }}>
                <div style={{
                  width: "40px",
                  height: "40px",
                  border: "4px solid #f3f3f3",
                  borderTop: "4px solid #1976D2",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                  margin: "0 auto"
                }} />
                <style>
                  {`
                    @keyframes spin {
                      0% { transform: rotate(0deg); }
                      100% { transform: rotate(360deg); }
                    }
                  `}
                </style>
              </div>
            )}

            <p style={{ fontSize: "14px", color: "#666", marginBottom: "15px" }}>
              Your bill will download automatically.
            </p>

            {!isDownloading && (
              <button
                onClick={downloadPDF}
                style={{
                  backgroundColor: "#1976D2",
                  color: "white",
                  border: "none",
                  padding: "10px 20px",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "bold",
                  marginTop: "10px"
                }}
              >
                Click to Download
              </button>
            )}
          </div>
        )}
      </div>
    );
  }


  return (
    <div style={fullScreenStyles.container}>
      <PDFViewer width="100%" height="100%" showToolbar>
        {PdfDoc}
      </PDFViewer>
    </div>
  );
};
